import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Loader2, Video, Link2, Unlink, CheckCircle2, KeyRound } from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const [gstatus, setGstatus] = useState(null);
  const [gloading, setGloading] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    if (pw.next !== pw.confirm) { toast.error("Le nuove password non coincidono"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password aggiornata");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Aggiornamento non riuscito"); }
    finally { setPwSaving(false); }
  };

  const loadGoogle = () =>
    api.get("/google/calendar/status").then((r) => setGstatus(r.data)).catch(() => setGstatus({ connected: false }));

  useEffect(() => { loadGoogle(); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("google");
    if (p === "connected") { toast.success("Account Google collegato"); loadGoogle(); window.history.replaceState({}, "", "/profilo"); }
    else if (p === "error") { toast.error("Collegamento Google non riuscito"); window.history.replaceState({}, "", "/profilo"); }
  }, []);

  const connectGoogle = async () => {
    setGloading(true);
    try { const { data } = await api.get("/google/calendar/connect"); window.location.href = data.auth_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setGloading(false); }
  };
  const disconnectGoogle = async () => {
    setGloading(true);
    try { await api.post("/google/calendar/disconnect"); toast.success("Account Google scollegato"); await loadGoogle(); }
    catch { toast.error("Errore"); } finally { setGloading(false); }
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Il mio profilo</h1>
          <p className="text-xs text-muted-foreground">Account e integrazioni personali</p>
        </div>
      </header>

      <div className="p-4 lg:p-8 space-y-4">
        <Card className="max-w-xl rounded-lg border-border p-6" data-testid="profile-card">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12"><AvatarFallback className="bg-secondary">{initials}</AvatarFallback></Avatar>
            <div>
              <div className="text-lg font-semibold" data-testid="profile-name">{user?.name}</div>
              <div className="text-sm text-muted-foreground" data-testid="profile-email">{user?.email}</div>
              <div className="text-[11px] uppercase tracking-widest text-primary mt-0.5">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>
        </Card>

        <Card className="max-w-xl rounded-lg border-border p-6" data-testid="change-password-card">
          <div className="flex items-center gap-2 text-sm font-semibold mb-1"><KeyRound className="h-4 w-4 text-primary" /> Cambia password</div>
          <p className="text-xs text-muted-foreground mb-4">Inserisci la password attuale e scegline una nuova (almeno 8 caratteri).</p>
          <div className="space-y-3">
            <div><Label className="text-xs">Password attuale</Label><Input type="password" value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} data-testid="pw-current" autoComplete="current-password" /></div>
            <div><Label className="text-xs">Nuova password</Label><Input type="password" value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} data-testid="pw-new" autoComplete="new-password" /></div>
            <div><Label className="text-xs">Conferma nuova password</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} data-testid="pw-confirm" autoComplete="new-password" /></div>
            <Button size="sm" onClick={changePassword} disabled={pwSaving || !pw.current || !pw.next || !pw.confirm} className="font-semibold" data-testid="pw-submit">
              {pwSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1.5" />} Aggiorna password
            </Button>
          </div>
        </Card>

        <Card className="max-w-xl rounded-lg border-border p-6" data-testid="google-calendar-card">
          <div className="flex items-center gap-2 text-sm font-semibold mb-1"><Video className="h-4 w-4 text-primary" /> Google Meet / Calendar</div>
          <p className="text-xs text-muted-foreground mb-4">
            Collega il <b>tuo</b> account Google: gli appuntamenti online dei lead a te assegnati verranno creati sul tuo calendario, con link Meet generato automaticamente.
          </p>
          {gstatus === null ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : gstatus.connected ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-sm text-green-600 font-medium" data-testid="google-connected">
                <CheckCircle2 className="h-4 w-4" /> Collegato{gstatus.email ? ` (${gstatus.email})` : ""}
              </span>
              <Button variant="outline" size="sm" onClick={disconnectGoogle} disabled={gloading} data-testid="google-disconnect-btn">
                <Unlink className="h-4 w-4 mr-1.5" /> Scollega
              </Button>
            </div>
          ) : (
            <div>
              <span className="text-sm text-muted-foreground">Nessun account collegato — i tuoi appuntamenti online non avranno il link Meet automatico.</span>
              <div className="mt-3">
                <Button size="sm" onClick={connectGoogle} disabled={gloading} className="font-semibold" data-testid="google-connect-btn">
                  {gloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />} Collega Google
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
